import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatBillingMoney } from "./format";
import { bindTrackToCurrentUser } from "@/lib/posthog";
import {
  BILLING_QUERY_KEY,
  addBillingBuilding,
  confirmPaymentMethodSetup,
  getBillingContext,
  getBillingInvoices,
  getBillingOverview,
  removeBillingBuilding,
  restoreBillingBuilding,
  saveBillingDetails,
  startPaymentMethodSetup,
  getBillingPlanCatalog,
  getBuildingSubscription,
  changeBuildingPlan,
  retryBuildingActivation,
} from "./repository";
import type { BillingDetailsInput, BuildingPlanTier } from "./types";

export const billingKeys = {
  all: BILLING_QUERY_KEY,
  context: (orgId: string) => [...billingKeys.all, orgId, "context"] as const,
  overview: (orgId: string) => [...billingKeys.all, orgId, "overview"] as const,
  invoices: (orgId: string) => [...billingKeys.all, orgId, "invoices"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  building: (orgId: string, buildingId: number) =>
    [...billingKeys.all, orgId, "building", buildingId] as const,
};

export function useBillingPlans(enabled = true) {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: getBillingPlanCatalog,
    enabled,
    staleTime: 60_000,
  });
}

export function useBuildingSubscription(buildingId: number, enabled = true) {
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = isReady ? currentOrganization?.id : undefined;
  return useQuery({
    queryKey: billingKeys.building(orgId ?? "none", buildingId),
    queryFn: () => getBuildingSubscription(buildingId),
    enabled: enabled && Boolean(orgId),
    staleTime: 15_000,
    refetchInterval: (query) =>
      query.state.data?.requested_tier || query.state.data?.activation_pending ? 5_000 : false,
  });
}

export function useRetryBuildingActivation() {
  const invalidate = useBillingRefresh();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, buildingId }: { orgId: string; buildingId: number }) =>
      retryBuildingActivation(orgId, buildingId),
    onMutate: (variables) => ({ track: bindTrackToCurrentUser(variables.orgId) }),
    onSuccess: async (result, variables, action) => {
      action?.track("building_activation_retried", {
        status: result.activation_pending ? "pending" : "applied",
      });
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({
          queryKey: ["building-storage-quota", String(variables.buildingId)],
          refetchType: "all",
        }),
      ]);
    },
  });
}

export function useChangeBuildingPlan() {
  const invalidate = useBillingRefresh();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      buildingId,
      tier,
    }: {
      orgId: string;
      buildingId: number;
      tier: BuildingPlanTier;
    }) => changeBuildingPlan(orgId, buildingId, tier),
    onMutate: (variables) => ({ track: bindTrackToCurrentUser(variables.orgId) }),
    onSuccess: async (result, variables, action) => {
      action?.track("building_plan_changed", { tier: variables.tier, status: result.status });
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({
          queryKey: ["building-storage-quota", String(variables.buildingId)],
          refetchType: "all",
        }),
      ]);
    },
  });
}

function availableOrgId(isReady: boolean, selectedOrgId?: string) {
  if (!isReady) return undefined;
  return selectedOrgId;
}

function requiredOrgId(orgId?: string) {
  if (!orgId) throw new Error("No organisation selected");
  return orgId;
}

// Loading is not an error. PermissionGuard has no pending state, so exposing an
// error while the organisation context is still settling would flash a route
// fallback during every cold load.
//
// Nor is a failed refetch: the cached context is the last thing the server
// actually said, and the key is per-organisation, so it cannot be another org's
// answer. Only a query that has never produced a context leaves the status
// genuinely unknown — one dropped background poll must not withhold anything.
function mutationStatusUnknown(
  selectedOrgId: string | undefined,
  isReady: boolean,
  queryLoading: boolean,
  queryError: boolean,
  hasContext: boolean
) {
  if (!selectedOrgId) return false;
  if (!isReady || queryLoading) return false;
  return queryError && !hasContext;
}

export function useBillingAccess() {
  const { currentOrganization, isReady } = useOrganizationContext();
  const selectedOrgId = currentOrganization?.id;
  const orgId = availableOrgId(isReady, selectedOrgId);
  const query = useQuery({
    queryKey: billingKeys.context(orgId ?? "none"),
    queryFn: () => getBillingContext(requiredOrgId(orgId)),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 3,
  });

  return {
    ...query,
    orgId,
    context: query.data ?? null,
    // A disabled query is pending-but-not-fetching in TanStack v5 (isLoading
    // false, data undefined), so "no organisation yet" must be reported as
    // loading — otherwise every consumer reads it as a settled empty result.
    isLoading: !isReady || !orgId || query.isLoading,
    isReadOnly: query.data?.is_read_only === true,
    detailsMissing: query.data?.details_missing === true,
    paymentMethodMissing: query.data?.payment_method_missing === true,
    isPilot: query.data?.is_pilot === true,
    mutationStatusUnknown: mutationStatusUnknown(
      selectedOrgId,
      isReady,
      query.isLoading,
      query.isError,
      query.data !== undefined
    ),
  };
}

export function useBillingOverview() {
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = isReady ? currentOrganization?.id : undefined;
  const query = useQuery({
    queryKey: billingKeys.overview(orgId ?? "none"),
    queryFn: () => {
      if (!orgId) throw new Error("No organisation selected");
      return getBillingOverview(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 15_000,
  });
  // !orgId keeps a disabled query out of the pages' error branch — see
  // useBillingAccess.
  return { ...query, isLoading: !isReady || !orgId || query.isLoading };
}

export function useBillingInvoices() {
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = isReady ? currentOrganization?.id : undefined;
  const query = useQuery({
    queryKey: billingKeys.invoices(orgId ?? "none"),
    queryFn: () => {
      if (!orgId) throw new Error("No organisation selected");
      return getBillingInvoices(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 30_000,
  });
  return { ...query, isLoading: !isReady || !orgId || query.isLoading };
}

/**
 * Re-read everything billing after a write, and WAIT for it.
 *
 * Two things here are load-bearing, and both were the reason a successful save
 * left the "Add your billing details" banner on screen until the page was
 * reloaded by hand.
 *
 * `refetchType: "all"` — the default is `"active"`, which refetches only the
 * queries that currently have a mounted observer. Every billing write is made
 * from ONE of the three pages, so the other two are inactive: saving the details
 * marks the overview stale without re-reading it, and returning to it inside the
 * `staleTime` window shows the pre-write answer. The context query is the one
 * that must never be stale — it feeds `BillingStatusProvider`, `usePermissions`
 * and the banners across the whole application, not just this section.
 *
 * The returned promise is awaited by the mutation. `onSuccess` may return one,
 * and `mutateAsync` does not resolve until it settles, so by the time a caller
 * shows its success toast the refetch has landed and the screen already agrees
 * with it — rather than the toast racing the banner it contradicts.
 */
export function useBillingRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: billingKeys.all, refetchType: "all" }),
      queryClient.invalidateQueries({ queryKey: ["organization-storage-buildings"] }),
      queryClient.invalidateQueries({ queryKey: ["organization-storage-usage"] }),
    ]);
  };
}

export function useSaveBillingDetails() {
  const invalidate = useBillingRefresh();
  return useMutation({
    mutationFn: ({ orgId, input }: { orgId: string; input: BillingDetailsInput }) =>
      saveBillingDetails(orgId, input),
    onSuccess: invalidate,
  });
}

export function useAddBillingBuilding() {
  const invalidate = useBillingRefresh();
  return useMutation({
    mutationFn: ({
      orgId,
      name,
      address,
      registryCode,
      tier,
      creationRequestId,
    }: {
      orgId: string;
      name: string;
      address: string;
      registryCode: string | null;
      tier: BuildingPlanTier;
      creationRequestId?: string;
    }) => addBillingBuilding(orgId, { name, address, registryCode, tier, creationRequestId }),
    onMutate: (variables) => ({ track: bindTrackToCurrentUser(variables.orgId) }),
    onSuccess: async (_result, _variables, action) => {
      action?.track("building_created", {});
      await invalidate();
    },
  });
}

export function useRemoveBillingBuilding() {
  const invalidate = useBillingRefresh();
  return useMutation({
    mutationFn: ({ orgId, buildingId }: { orgId: string; buildingId: number }) =>
      removeBillingBuilding(orgId, buildingId),
    onSuccess: invalidate,
  });
}

/**
 * Ask for a Stripe Checkout setup session.
 *
 * Deliberately does NOT invalidate on success: nothing has changed yet, and the
 * caller sends the browser to Stripe the moment it has the URL.
 */
export function useStartPaymentMethodSetup() {
  return useMutation({
    mutationFn: ({
      orgId,
      returnUrl,
      locale,
    }: {
      orgId: string;
      returnUrl: string;
      locale?: string;
    }) => startPaymentMethodSetup(orgId, returnUrl, locale),
  });
}

export function useConfirmPaymentMethodSetup() {
  const invalidate = useBillingRefresh();
  return useMutation({
    mutationFn: ({ orgId, sessionId }: { orgId: string; sessionId: string }) =>
      confirmPaymentMethodSetup(orgId, sessionId),
    onSuccess: invalidate,
  });
}

export function useRestoreBillingBuilding() {
  const invalidate = useBillingRefresh();
  return useMutation({
    mutationFn: ({ orgId, buildingId }: { orgId: string; buildingId: number }) =>
      restoreBillingBuilding(orgId, buildingId),
    onSuccess: invalidate,
  });
}

/**
 * Money formatter bound to the active UI language.
 *
 * Estonian writes "290,00 €" where English writes "€290.00", so the locale has
 * to follow the language rather than sit pinned in the pure formatter.
 */
export function useBillingMoney() {
  const { language } = useLanguage();
  return (cents: number, currency = "EUR") => formatBillingMoney(cents, currency, language);
}
